import React, { useState, useEffect } from 'react';
import { useNavigate, createSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Search, MapPin, Users, Bed, Bath, Star, Heart, Zap, CalendarIcon, X } from 'lucide-react';
import { propertyAPI, calendarAPI } from '../services/api';
import { format, isWithinInterval, parseISO, addDays } from 'date-fns';
import { cn } from '../lib/utils';
import VerificationBadge from './VerificationBadge';

const GuestBrowse = () => {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCity, setSelectedCity] = useState('all');
  const [guests, setGuests] = useState(1);
  const [priceRange, setPriceRange] = useState('all');
  const [propertyType, setPropertyType] = useState('all');
  const [favorites, setFavorites] = useState([]);
  const [cities, setCities] = useState(['all']);
  
  // Date filtering state
  const [checkInDate, setCheckInDate] = useState(null);
  const [checkOutDate, setCheckOutDate] = useState(null);
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [checkOutOpen, setCheckOutOpen] = useState(false);
  const [availabilityCache, setAvailabilityCache] = useState({});
  const [checkingAvailability, setCheckingAvailability] = useState(false);

  // Fetch properties on mount and when filters change
  useEffect(() => {
    fetchProperties();
  }, [selectedCity, propertyType, priceRange, guests]);

  // Filter by availability when dates change
  useEffect(() => {
    if (checkInDate && checkOutDate && properties.length > 0) {
      filterByAvailability();
    } else {
      // Apply just the text search filter
      applySearchFilter(properties);
    }
  }, [checkInDate, checkOutDate, properties, searchTerm]);

  const fetchProperties = async () => {
    setLoading(true);
    try {
      const filters = {
        city: selectedCity,
        propertyType: propertyType,
        guests: guests
      };

      if (priceRange !== 'all') {
        if (priceRange === 'budget') {
          filters.maxPrice = 150;
        } else if (priceRange === 'mid') {
          filters.minPrice = 150;
          filters.maxPrice = 250;
        } else if (priceRange === 'luxury') {
          filters.minPrice = 250;
        }
      }

      const data = await propertyAPI.getPublishedProperties(filters);
      setProperties(data);

      // Extract unique cities
      const uniqueCities = ['all', ...new Set(data.map(p => p.location?.city).filter(Boolean))];
      setCities(uniqueCities);
      
      // Apply initial search filter
      applySearchFilter(data);
    } catch (error) {
      console.error('Error fetching properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const applySearchFilter = (props) => {
    const filtered = props.filter(property => {
      const matchesSearch = property.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           property.location?.city?.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    });
    setFilteredProperties(filtered);
  };

  const filterByAvailability = async () => {
    if (!checkInDate || !checkOutDate) return;
    
    setCheckingAvailability(true);
    
    try {
      const availableProperties = [];
      
      for (const property of properties) {
        // Check cache first
        if (availabilityCache[property._id]) {
          const isAvailable = checkPropertyAvailability(
            availabilityCache[property._id],
            checkInDate,
            checkOutDate
          );
          if (isAvailable) {
            availableProperties.push(property);
          }
          continue;
        }
        
        // Fetch availability from API
        try {
          const availability = await calendarAPI.getAvailability(property._id);
          
          // Cache the result
          setAvailabilityCache(prev => ({
            ...prev,
            [property._id]: availability.unavailableDates || []
          }));
          
          const isAvailable = checkPropertyAvailability(
            availability.unavailableDates || [],
            checkInDate,
            checkOutDate
          );
          
          if (isAvailable) {
            availableProperties.push(property);
          }
        } catch (err) {
          // If availability check fails, include the property (optimistic)
          availableProperties.push(property);
        }
      }
      
      // Apply search filter to available properties
      const finalFiltered = availableProperties.filter(property => {
        const matchesSearch = property.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             property.location?.city?.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesSearch;
      });
      
      setFilteredProperties(finalFiltered);
    } finally {
      setCheckingAvailability(false);
    }
  };

  const checkPropertyAvailability = (unavailableDates, checkIn, checkOut) => {
    if (!unavailableDates || unavailableDates.length === 0) return true;
    
    const checkInStr = format(checkIn, 'yyyy-MM-dd');
    const checkOutStr = format(checkOut, 'yyyy-MM-dd');
    
    for (const period of unavailableDates) {
      const periodStart = period.start;
      const periodEnd = period.end;
      
      // Check for overlap
      if (
        (checkInStr >= periodStart && checkInStr < periodEnd) ||
        (checkOutStr > periodStart && checkOutStr <= periodEnd) ||
        (checkInStr <= periodStart && checkOutStr >= periodEnd)
      ) {
        return false;
      }
    }
    
    return true;
  };

  const toggleFavorite = (propertyId) => {
    setFavorites(prev => 
      prev.includes(propertyId) 
        ? prev.filter(id => id !== propertyId)
        : [...prev, propertyId]
    );
  };

  const buildPropertyUrl = (propertyId) => {
    const params = {};
    if (checkInDate) params.checkIn = format(checkInDate, 'yyyy-MM-dd');
    if (checkOutDate) params.checkOut = format(checkOutDate, 'yyyy-MM-dd');
    const query = Object.keys(params).length > 0 ? `?${createSearchParams(params)}` : '';
    return `/property/${propertyId}${query}`;
  };

  const clearDates = () => {
    setCheckInDate(null);
    setCheckOutDate(null);
    applySearchFilter(properties);
  };

  const handleSearch = () => {
    if (checkInDate && checkOutDate) {
      filterByAvailability();
    } else {
      applySearchFilter(properties);
    }
  };

  return (
    <div className="space-y-6">
      {/* Search & Date Filters */}
      <Card className="border-0 shadow-lg">
        <CardContent className="p-6">
          <div className="grid md:grid-cols-5 gap-4">
            {/* Search Input */}
            <div className="md:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <Input
                  placeholder="Search by city or property name"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 h-12"
                  data-testid="browse-search-input"
                />
              </div>
            </div>
            
            {/* Check-in Date */}
            <div>
              <Popover open={checkInOpen} onOpenChange={setCheckInOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full h-12 justify-start text-left font-normal",
                      !checkInDate && "text-muted-foreground"
                    )}
                    data-testid="browse-checkin-btn"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {checkInDate ? format(checkInDate, "MMM d, yyyy") : "Check-in"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={checkInDate}
                    onSelect={(date) => {
                      setCheckInDate(date);
                      setCheckInOpen(false);
                      if (date && checkOutDate && date >= checkOutDate) {
                        setCheckOutDate(addDays(date, 1));
                      }
                    }}
                    disabled={(date) => date < new Date()}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            
            {/* Check-out Date */}
            <div>
              <Popover open={checkOutOpen} onOpenChange={setCheckOutOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full h-12 justify-start text-left font-normal",
                      !checkOutDate && "text-muted-foreground"
                    )}
                    data-testid="browse-checkout-btn"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {checkOutDate ? format(checkOutDate, "MMM d, yyyy") : "Check-out"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={checkOutDate}
                    onSelect={(date) => {
                      setCheckOutDate(date);
                      setCheckOutOpen(false);
                    }}
                    disabled={(date) => date <= (checkInDate || new Date())}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Search Button */}
            <div>
              <Button 
                className="w-full h-12 bg-[#CB3B3B] hover:bg-[#CB3B3B]/90"
                onClick={handleSearch}
                disabled={checkingAvailability}
                data-testid="browse-search-btn"
              >
                {checkingAvailability ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                ) : (
                  <Search className="mr-2 h-4 w-4" />
                )}
                Search
              </Button>
            </div>
          </div>
          
          {/* Active Date Filter Indicator */}
          {(checkInDate || checkOutDate) && (
            <div className="mt-4 flex items-center gap-2">
              <span className="text-sm text-gray-600">Filtering by dates:</span>
              <span className="bg-[#1F3A68] text-white text-sm px-3 py-1 rounded-full flex items-center gap-2">
                {checkInDate && format(checkInDate, "MMM d")} - {checkOutDate && format(checkOutDate, "MMM d")}
                <button onClick={clearDates} className="hover:bg-white/20 rounded-full p-0.5">
                  <X size={14} />
                </button>
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Advanced Filters */}
      <Card className="border-0 shadow-md">
        <CardContent className="p-4">
          <div className="grid md:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <MapPin className="inline w-4 h-4 mr-1" />
                City
              </label>
              <select
                value={selectedCity}
                onChange={(e) => setSelectedCity(e.target.value)}
                className="w-full h-11 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F3A68] focus:border-transparent"
                data-testid="browse-city-filter"
              >
                {cities.map(city => (
                  <option key={city} value={city}>
                    {city === 'all' ? 'All Cities' : city}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Property Type
              </label>
              <select
                value={propertyType}
                onChange={(e) => setPropertyType(e.target.value)}
                className="w-full h-11 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F3A68] focus:border-transparent"
                data-testid="browse-type-filter"
              >
                <option value="all">All Types</option>
                <option value="Apartment">Apartment</option>
                <option value="House">House</option>
                <option value="Loft">Loft</option>
                <option value="Studio">Studio</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Price Range
              </label>
              <select
                value={priceRange}
                onChange={(e) => setPriceRange(e.target.value)}
                className="w-full h-11 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F3A68] focus:border-transparent"
                data-testid="browse-price-filter"
              >
                <option value="all">All Prices</option>
                <option value="budget">Under £150</option>
                <option value="mid">£150 - £250</option>
                <option value="luxury">Above £250</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <Users className="inline w-4 h-4 mr-1" />
                Guests
              </label>
              <Input
                type="number"
                min="1"
                value={guests}
                onChange={(e) => setGuests(parseInt(e.target.value) || 1)}
                className="h-10"
                data-testid="browse-guests-filter"
              />
            </div>

            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={() => {
                  setSelectedCity('all');
                  setPropertyType('all');
                  setPriceRange('all');
                  setSearchTerm('');
                  setGuests(1);
                  clearDates();
                }}
                className="w-full h-10"
                data-testid="browse-clear-filters"
              >
                Clear All
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results Count */}
      <div className="flex items-center justify-between">
        <p className="text-gray-600 text-lg">
          {loading || checkingAvailability ? (
            'Searching properties...'
          ) : (
            <>
              <span className="font-bold text-[#1F3A68]">{filteredProperties.length}</span> 
              {filteredProperties.length === 0 ? ' No properties found' : ' properties available'}
              {checkInDate && checkOutDate && (
                <span className="text-sm ml-2 text-gray-500">
                  for {format(checkInDate, "MMM d")} - {format(checkOutDate, "MMM d")}
                </span>
              )}
            </>
          )}
        </p>
      </div>

      {/* Property Grid */}
      {loading ? (
        <div className="text-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1F3A68] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading properties...</p>
        </div>
      ) : filteredProperties.length === 0 ? (
        <Card className="p-12 text-center">
          <div className="text-gray-400 mb-4">
            <Search size={64} className="mx-auto" />
          </div>
          <h3 className="text-2xl font-bold text-gray-700 mb-2">No properties found</h3>
          <p className="text-gray-600 mb-6">
            {checkInDate && checkOutDate 
              ? 'No properties available for the selected dates. Try different dates or clear the date filter.'
              : properties.length === 0 
                ? 'No properties have been published yet. Check back soon!' 
                : 'Try adjusting your filters or search terms'}
          </p>
          {(checkInDate || checkOutDate) && (
            <Button onClick={clearDates} variant="outline">
              Clear Date Filter
            </Button>
          )}
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProperties.map(property => (
            <Card
              key={property._id}
              className="overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer group"
              onClick={() => navigate(buildPropertyUrl(property._id))}
              data-testid={`property-card-${property._id}`}
            >
              <div className="relative">
                <img
                  src={property.images?.[0] || 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=1200&h=800&fit=crop'}
                  alt={property.title}
                  className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFavorite(property._id);
                  }}
                  className="absolute top-3 right-3 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors"
                  data-testid={`favorite-btn-${property._id}`}
                >
                  <Heart
                    size={20}
                    className={favorites.includes(property._id) ? 'fill-red-500 text-red-500' : 'text-gray-600'}
                  />
                </button>
                {property.instantBook && (
                  <div className="absolute top-3 left-3 bg-[#1F3A68] text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
                    <Zap size={14} />
                    Instant Book
                  </div>
                )}
              </div>

              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3 className="font-bold text-lg text-gray-900 mb-1 line-clamp-1">
                      {property.title}
                    </h3>
                    <p className="text-gray-600 text-sm flex items-center gap-1">
                      <MapPin size={14} />
                      {property.location?.city || 'Unknown'}
                    </p>
                  </div>
                  {property.rating > 0 && (
                    <div className="flex items-center gap-1 bg-[#1F3A68] text-white px-2 py-1 rounded-lg">
                      <Star size={14} fill="white" />
                      <span className="text-sm font-semibold">{property.rating}</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                  <span className="flex items-center gap-1">
                    <Users size={16} />
                    {property.capacity?.guests || 0}
                  </span>
                  <span className="flex items-center gap-1">
                    <Bed size={16} />
                    {property.capacity?.bedrooms || 0}
                  </span>
                  <span className="flex items-center gap-1">
                    <Bath size={16} />
                    {property.capacity?.bathrooms || 0}
                  </span>
                </div>

                <div className="flex items-center gap-2 flex-wrap mb-2">
                  {property.verificationStatus && (
                    <VerificationBadge status={property.verificationStatus} size="sm" />
                  )}
                  {property.hasSmartLock && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-700">
                      🔒 Smart Lock
                    </span>
                  )}
                </div>

                <div className="border-t pt-3">
                  <div className="flex items-end justify-between">
                    <div>
                      <span className="text-2xl font-bold text-[#1F3A68]">
                        £{property.pricing?.nightlyRate || 0}
                      </span>
                      <span className="text-gray-600 text-sm"> / night</span>
                    </div>
                    <Button
                      size="sm"
                      className="bg-[#CB3B3B] hover:bg-[#CB3B3B]/90"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(buildPropertyUrl(property._id));
                      }}
                      data-testid={`view-details-btn-${property._id}`}
                    >
                      View Details
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default GuestBrowse;
